import React, { useEffect, useState, useRef } from "react";
import { BASE, BORDER, ERROR, GREY } from "../../utils/constants/colors";
import { fontSize, fontWeight, lineHeight, SHADOW } from "../../utils/constants/UI";
import { borderRadius } from "../../utils/constants/UI/uiConstants";
import IconComponent from "../Icon/IconComponent";
import Typography from "../Typography/Typography";
import { STRINGS } from "./strings";
import classes from './Input.module.css'

// Monotonic counter for generating unique fallback input ids (React 17 has no useId).
let inputFieldSeq = 0;

const SIZE_MAPPING = {
  xs34: {
    padding: "8.5px 10px 8.5px 0px",
    fontSize: fontSize.XS,
    fontWeight: fontWeight.REGULAR,
    lineHeight: lineHeight.XS,
    iconSize: fontSize.MD,
  },
  xs36: {
    padding: "9.5px 10px 9.5px 0px",
    fontSize: fontSize.XS,
    fontWeight: fontWeight.REGULAR,
    lineHeight: lineHeight.XS,
    iconSize: fontSize.MD,
  },
  sm36: {
    padding: "8.5px 10px 8.5px 0px",
    fontSize: fontSize.SM,
    fontWeight: fontWeight.REGULAR,
    lineHeight: lineHeight.SM,
    iconSize: fontSize.MD,
  },
  md40: {
    padding: "10.5px 10px 10.5px 0px",
    fontSize: fontSize.SM,
    fontWeight: fontWeight.REGULAR,
    lineHeight: lineHeight.SM,
    iconSize: fontSize.MD,
  },
  lg44: {
    padding: "11px 10px 11px 0px",
    fontSize: fontSize.MD,
    fontWeight: fontWeight.REGULAR,
    lineHeight: lineHeight.MD,
    iconSize: fontSize.XL,
  },
  default: {
    backgroundColor: BASE.WHITE,
    border: `1px solid ${BORDER.HOVER}`,
  },
  focused: {
    backgroundColor: BASE.WHITE,
    border: `1px solid ${GREY.PRIMARY}`,
    boxShadow: SHADOW.input,
  },
  filled: {
    boxShadow: "",
    backgroundColor: BASE.WHITE,
    border: `1px solid ${BORDER.DARK}`,
  },
  error: {
    boxShadow: "",
    backgroundColor: BASE.WHITE,
    border: `1px solid ${ERROR.W_500}`,
  },
  disabled: {
    backgroundColor: BORDER.DISABLED,
    border: `1px solid ${BASE.TRANSPARENT}`,
  },
};

const InputField = ({
  type = "text",
  size,
  LeftIcon,
  RightIcon,
  variant,
  disabled,
  errormessage = "",
  onFocus,
  onBlur,
  // Pulled out of ...props so they are NOT spread onto the native <input> as
  // invalid DOM attributes (they are consumed explicitly below).
  id,
  leftOnClick,
  rightOnClick,
  leftIconLabel,
  rightIconLabel,
  iscapitalize,
  isDomain,
  ...props
}) => {

  const [inputState, setInputState] = useState(variant || "default");

  // Stable unique id per instance (used when the caller doesn't pass one) so
  // multiple InputFields don't collide on a shared DOM id.
  const autoIdRef = useRef(null);
  if (autoIdRef.current === null) autoIdRef.current = `inputField-${inputFieldSeq++}`;
  const inputId = id || autoIdRef.current;

  // When an icon has a click handler it is an interactive control: return the
  // a11y attrs that make it keyboard-operable (Enter/Space) with an accessible
  // name. NOTE: no `style` here — the caller merges `cursor` into the icon's own
  // style object so token sizing/color (styles.iconStyle/rightIconStyle) is kept.
  const makeIconInteractive = (handler, label) =>
    handler
      ? {
          role: "button",
          tabIndex: 0,
          "aria-label": label,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handler(e);
            }
          },
        }
      : {};

  const styles = {
    mainContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      width: "100%",
    },
    inputField: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      width: "100%",
      border: "none",
      height: "100%",
      borderRadius: borderRadius.primary,
      color: GREY.SECONDARY,
      fontWeight: SIZE_MAPPING[size]?.fontWeight,
      fontSize: SIZE_MAPPING[size]?.fontSize,
      lineHeight: SIZE_MAPPING[size]?.lineHeight,
      padding: SIZE_MAPPING[size]?.padding,
      fontStyle: "normal",
      cursor: inputState === "disabled" && "not-allowed",
      textTransform: (iscapitalize ? "capitalize" : "none"),
      backgroundColor: SIZE_MAPPING[inputState]?.backgroundColor,
      // Placeholder color is handled by Input.module.css (a nested '&::placeholder'
      // key in a React inline-style object is silently ignored), so it's not set here.
    },
    iconStyle: {
      fontSize: `${SIZE_MAPPING[size]?.iconSize}`,
      color: (disabled ? GREY.DISABLED : GREY.TERTIARY),
    },
    rightIconStyle: {
      fontSize: `${SIZE_MAPPING[size]?.iconSize}`,
      color: (disabled ? GREY.DISABLED : GREY.TERTIARY),
    },
    xs34: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "4px",
      borderRadius: borderRadius.primary,
      padding: (isDomain ? "0px 0px 0px 10px" : "0px 10px"),
      boxShadow: inputState === "focused" &&
        SIZE_MAPPING[inputState].boxShadow,
      border: SIZE_MAPPING[inputState]?.border,
      cursor: inputState === "disabled" ? "not-allowed" : "pointer",
      backgroundColor: SIZE_MAPPING[inputState]?.backgroundColor,

    },
    xs36: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "4px",
      borderRadius: borderRadius.primary,
      padding: (isDomain ? "0px 0px 0px 10px" : "0px 10px"),
      boxShadow: inputState === "focused" &&
        SIZE_MAPPING[inputState].boxShadow,
      cursor: inputState === "disabled" ? "not-allowed" : "pointer",
      border: SIZE_MAPPING[inputState]?.border,
      backgroundColor: SIZE_MAPPING[inputState]?.backgroundColor,

    },
    sm36: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "4px",
      borderRadius: borderRadius.primary,
      padding: (isDomain ? "0px 0px 0px 10px" : "0px 10px"),
      boxShadow: inputState === "focused" &&
        SIZE_MAPPING[inputState].boxShadow,
      cursor: inputState === "disabled" ? "not-allowed" : "pointer",
      border: SIZE_MAPPING[inputState]?.border,
      backgroundColor: SIZE_MAPPING[inputState]?.backgroundColor,

    },
    md40: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "4px",
      borderRadius: borderRadius.primary,
      padding: (isDomain ? "0px 0px 0px 10px" : "0px 10px"),
      boxShadow: inputState === "focused" &&
        SIZE_MAPPING[inputState].boxShadow,
      border: SIZE_MAPPING[inputState]?.border,
      cursor: inputState === "disabled" ? "not-allowed" : "pointer",
      backgroundColor: SIZE_MAPPING[inputState]?.backgroundColor,

    },
    lg44: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "4px",
      borderRadius: borderRadius.primary,
      padding: (isDomain ? "0px 0px 0px 10px" : "0px 10px"),
      boxShadow: inputState === "focused" &&
        SIZE_MAPPING[inputState].boxShadow,
      border: SIZE_MAPPING[inputState]?.border,
      cursor: inputState === "disabled" ? "not-allowed" : "pointer",
      backgroundColor: SIZE_MAPPING[inputState]?.backgroundColor,

    },
    domainLabelContainer: {
      padding: "8px 10px",
      backgroundColor: BASE.BACKGROUND_LIGHT,
      borderRadius: `${borderRadius.none} ${borderRadius.primary} ${borderRadius.primary} ${borderRadius.none}`,
    },
  };

  useEffect(() => {
    setInputState(variant || "default");
  }, [variant]);

  const inputHandler = (variantVal, inputValue) => {
    if (variantVal === "blurred" && variant !== "error") {
      if (inputValue) {
        setInputState("filled");
      } else {
        setInputState("default");
      }
    } else {
      if (variant !== "error") {
        setInputState(variantVal);
      }
    }
  };

  return (
    <div style={styles.mainContainer}>
      <div style={styles[size]} className={classes[inputState]} >
        {LeftIcon && (
          <IconComponent
            iconLabel={LeftIcon}
            style={{ ...styles.iconStyle, ...(leftOnClick ? { cursor: "pointer" } : {}) }}
            onClick={leftOnClick}
            {...makeIconInteractive(leftOnClick, leftIconLabel)}
          />
        )}
        <input
          autoComplete={type === 'text' ? "off" : "on"}
          id={inputId}
          type={type || "text"}
          style={styles.inputField}
          {...props}
          onFocus={(event) => { inputHandler("focused"); onFocus?.(event); }}
          onBlur={(event) => { inputHandler("blurred", event.target.value); onBlur?.(event); }}
          disabled={disabled || variant === "disabled"}
        />
        {RightIcon && (
          <IconComponent
            iconLabel={RightIcon}
            style={{ ...styles.rightIconStyle, ...(rightOnClick ? { cursor: "pointer" } : {}) }}
            onClick={rightOnClick}
            {...makeIconInteractive(rightOnClick, rightIconLabel)}
          />
        )}
        {isDomain && (
          <div style={styles.domainLabelContainer}>
            <Typography
              color={GREY.TERTIARY}
              fontWeight={fontWeight.REGULAR}
              size={"sm"}
              label={STRINGS.DOMAIN_LABEL}
            />
          </div>
        )}
      </div>
      {variant === "error" && errormessage && (
        <Typography
          label={errormessage}
          color={ERROR.W_500}
          size={"xs"}
          fontWeight={fontWeight.REGULAR}
        />
      )}
    </div>
  );
};

export default InputField;

// ------------- NOTE ----------------
// This component should only be used for text, passowrd and email type.
// If there is need to add color or any other attribute in InputField then please use SIZE_MAPPING object.
// Add key into object and use it with props according to size or variant.
// Props Description:
// type :- This is used for type of input, default input state is text.
// size :- This is used for size of input mentioned in Figma.
// LeftIcon :- Icon displayed on the left of input field.
// RightIcon :- Icon displayed on the right of input field.
// variant :- This will mainly used for only two states of input. And this two states are error and disabled state.
// leftOnClick : This prop will contain onClick Function on the left Icon if needed.
// rightOnClick : This prop will contain onClick Function on the right Icon if needed.
// errorMessage: Error message you want to display.
// isDomain: Used for dispaying domain name at the end of the field.
